import * as React from 'react';
import styled, { css } from 'styled-components';
import { FixedRatio, mqMin, breakpointSizes, BreakpointNames } from '@packages/ui';
import {
  BaseModuleImage,
  ImageStyle,
  ResponsiveImageSize,
  publicAssetsImageSrcToCdnImageSrc,
  ImageCDNProvider,
} from '@packages/common';

import { CallToAction } from '../CallToAction/CallToAction';

type PropsWithImageStyle = { imageStyle?: BaseModuleImage['imageStyle'] };

const applyImageStyle = ({ imageStyle }: PropsWithImageStyle) =>
  imageStyle === ImageStyle.ROUNDED_SHADOW
    ? css`
        border-radius: 6px;
        overflow: hidden;
        box-shadow: 0px 0px 6px 4px rgba(43, 18, 2, 0.15);
      `
    : '';

export const StyledCallToAction = styled(CallToAction)<PropsWithImageStyle>`
  user-select: none;
  ${applyImageStyle};
`;

const ImageContaierCSS = css`
  width: 100%;
  user-select: none;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    user-select: none;
  }
`;

const ImageContaier = styled.div`
  ${ImageContaierCSS}
`;

const Picture = styled.picture`
  ${ImageContaierCSS}
`;

const ImageSource: React.FC<ResponsiveImageSize & { cdnProvider: ImageCDNProvider }> = ({
  src,
  breakpoint,
  cdnProvider,
}) => {
  const media = mqMin((breakpointSizes as any)[breakpoint]);
  const srcSet = publicAssetsImageSrcToCdnImageSrc(src, cdnProvider);
  return <source srcSet={srcSet} media={media} />;
};

export type BaseImageProps = BaseModuleImage & {
  className?: string;
  onClick?: (item: BaseImageProps) => void;
  getRatio?: (item: BaseImageProps) => number | number[];
  cdnProvider?: ImageCDNProvider;
};

export class BaseImage extends React.PureComponent<BaseImageProps> {
  static defaultProps: Pick<BaseImageProps, 'getRatio'> = {
    getRatio: ({ image }: BaseImageProps) => image.ratio,
  };

  handleClick = () => {
    const { onClick } = this.props;
    if (onClick) {
      onClick(this.props);
    }
  };

  render() {
    const { image, cta, onClick, getRatio, className, imageStyle, cdnProvider } = this.props;
    const { src, alt, title, sizes } = image;
    const extension = src?.includes('.') ? src?.split('.').pop() : 'jpg';
    const smallSrc = publicAssetsImageSrcToCdnImageSrc(src, cdnProvider);
    const otherSizes = sizes.filter((size) => size.breakpoint !== BreakpointNames.sm);

    return (
      <StyledCallToAction {...cta} onClick={onClick && this.handleClick} className={className} imageStyle={imageStyle}>
        <FixedRatio ratio={getRatio && getRatio(this.props)} isHeightLimited={false}>
          {otherSizes.length ? (
            <Picture>
              {otherSizes
                .filter((size) => size.breakpoint !== BreakpointNames.sm)
                .map((source: ResponsiveImageSize) => (
                  <ImageSource key={source.breakpoint} cdnProvider={cdnProvider} {...source} />
                ))}
              <img loading="lazy" src={smallSrc} alt={alt} title={title} />
            </Picture>
          ) : (
            <ImageContaier>
              <img loading="lazy" src={smallSrc} alt={alt} title={title} />
            </ImageContaier>
          )}
        </FixedRatio>
      </StyledCallToAction>
    );
  }
}
